import { useGetUrgentStudentData } from '@actions';
import { BellOutlined, FireOutlined } from '@ant-design/icons';
import { ReactComponent as MenuIcon } from '@assets/icons/burger-menu.svg';
import { useAuthState } from '@contexts/AuthContext';
import { UserRole } from '@shared/enums';
import { BASE_URL } from '@shared/frontendEnv';
import styles from '@styles/components/header.module.scss';
import { Alert, Spin } from 'antd';
import { Badge, Button } from 'antd';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import useToast from '../../hooks/useToast';
import AccountDropdown from './Components/AccountDropdown';
import LogoButton from './Components/LogoButton';
import MobileMenuDropdown from './Components/MobileMenuDropdown';
import { useMobileState } from './MobileContext';

const Header = () => {
  const user = useAuthState();
  const harvesterLink = `${BASE_URL}/referral/${user?.harvesterToken}`;
  const isBdr = user.roles?.includes(UserRole.BDR);

  const toast = useToast();

  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const isMobile = useMobileState();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const { data, isLoading } = useGetUrgentStudentData({ enabled: !isBdr });

  const handleCopyClick = () => {
    navigator.clipboard
      .writeText(harvesterLink)
      .then(() => {
        toast.success('Link copied to clipboard!');
      })
      .catch((err) => {
        toast.error('Failed to copy the link.');
      });
  };

  useEffect(() => {
    setMobileMenuVisible(false);
  }, [pathname]);

  const isImpersonating = () => !!localStorage.getItem('impersonate-user-id');

  const stopImpersonating = () => {
    localStorage.removeItem('impersonate-user-id');
    localStorage.removeItem('userRole');
    localStorage.setItem('menuType', UserRole.Admin.toString());
    queryClient.clear();
    toast.info('Stopping impersonating...');

    window.location.href = '/';
  };

  const mobileHeader = (
    <>
      <MobileMenuDropdown visible={mobileMenuVisible}>
        <MenuIcon
          style={{ cursor: 'pointer' }}
          onClick={() => setMobileMenuVisible((prev) => !prev)}
        />
      </MobileMenuDropdown>
      <LogoButton width="50px" height="25px" />
      <div style={{ width: '20px', height: '20px' }}></div>
    </>
  );

  const desktopHeader = (
    <div className={styles.headerContainer}>
      <div className={styles.itemsGroup}>
        {!isBdr && (
          <>
            <a
              href={`${BASE_URL}/admin/enrollments?page=1&limit=10&hideEnrollmentsWithNoSessions=unchecked&showMentorsAsStudents=checked&showDeactivatedStudents=unchecked&stage=0`}
            >
              <div className={styles.badgeItem}>
                <Badge
                  count={isLoading ? <Spin size="small" /> : data?.notStartedCount}
                  overflowCount={999}
                  showZero
                  style={{
                    backgroundColor: isLoading
                      ? 'transparent'
                      : (data?.notStartedCount || 0) > 0
                      ? 'orange'
                      : 'gray',
                  }}
                  offset={[16, 0]}
                >
                  <BellOutlined style={{ fontSize: '1.5rem', color: 'orange' }} />
                </Badge>
                <span className={styles.label}>Not Started</span>
              </div>
            </a>
            <a
              href={`${BASE_URL}/admin/enrollments?page=1&limit=10&hideEnrollmentsWithNoSessions=unchecked&showMentorsAsStudents=checked&showDeactivatedStudents=unchecked&urgency=now`}
            >
              <div className={styles.badgeItem}>
                <Badge
                  count={isLoading ? <Spin size="small" /> : data?.urgencyCount}
                  overflowCount={999}
                  showZero
                  style={{
                    backgroundColor: isLoading
                      ? 'transparent'
                      : (data?.urgencyCount || 0) > 0
                      ? 'red'
                      : 'gray',
                  }}
                  offset={[16, 0]}
                >
                  <FireOutlined style={{ fontSize: '1.5rem', color: 'red' }} />
                </Badge>
                <span className={styles.label}>Needs mentor now</span>
              </div>
            </a>
          </>
        )}

        <Button type="link" onClick={handleCopyClick} style={{ fontSize: '12px' }}>
          Copy Referral
        </Button>

        <AccountDropdown />
      </div>
    </div>
  );

  return (
    <>
      {isImpersonating() && (
        <Alert
          message="You are currently impersonating another user's account. Click to stop."
          type="info"
          onClick={stopImpersonating}
          showIcon
          style={{ cursor: 'pointer' }}
        />
      )}

      <div className={styles.header}>{isMobile ? mobileHeader : desktopHeader}</div>
    </>
  );
};

export default Header;
